import { infiniteQueryFetchNextPage } from '@repo/sdk'
import type { AdminLink } from '@repo/sdk/gen'
import { PageLayout } from '@repo/ui/components/page-layout'
import { QueryBreadcrumbs } from '@repo/ui/components/query-breadcrumbs'
import { RefreshButton } from '@repo/ui/components/refresh-button'
import { getErrorCode } from '@repo/ui/utils'
import {
	useInfiniteQuery,
	useQuery,
	useQueryClient
} from '@tanstack/react-query'
import { createFileRoute, stripSearchParams } from '@tanstack/react-router'
import { linkPolicyQueryOptions } from '@/modules/file-manager/query/query-options'
import { makeQueryKeys } from '@/query/query-keys'
import { LINK_VIEW_STATES } from '@/routes/(private)/(shell)/-lib/link-views'
import { AdminLinksEmpty } from '@/routes/(private)/(shell)/admin/links/-components/admin-links-empty'
import { AdminLinksFilters } from '@/routes/(private)/(shell)/admin/links/-components/admin-links-filters'
import { AdminLinksTable } from '@/routes/(private)/(shell)/admin/links/-components/admin-links-table'
import { adminLinksSearchSchema } from '@/routes/(private)/(shell)/admin/links/-lib/admin-link-search'
import {
	linksOrganizationQueryOptions,
	MAX_PAGE_SIZE
} from '@/routes/(private)/(shell)/admin/links/-query/query-options'
import { usePageSize } from '@/stores/use-page-size'

export const Route = createFileRoute('/(private)/(shell)/admin/links/')({
	component: AdminLinks,
	validateSearch: adminLinksSearchSchema,
	search: {
		middlewares: [stripSearchParams(adminLinksSearchSchema.parse({}))]
	},
	staticData: {
		breadcrumb: 'admin-links:page_title'
	}
})

/**
 * The admin-facing half of public links: every public link anybody in the
 * organization has created, whoever created it, with a way to shut one off or
 * shut off everything one person shared.
 *
 * **`resourceName` and nothing else.** `AdminLink` carries no `token` and no
 * route to the node — an administrator can see that something is shared and
 * turn it off, never open what it points to. This page never builds a link to
 * the resource from `nodeId`; there is nothing here that could.
 */
function AdminLinks() {
	const queryClient = useQueryClient()
	const params = Route.useSearch()
	const pageSize = usePageSize((state) => state.pageSize)

	const viewStates = LINK_VIEW_STATES[params.view]
	const listQuery = {
		limit: Math.min(pageSize, MAX_PAGE_SIZE),
		creatorId: params.creatorId,
		kind: params.kind === 'all' ? undefined : params.kind,
		state: viewStates && [...viewStates],
		expiresWithinDays: params.expiresWithinDays,
		hasPassword: params.hasPassword
	}
	const links = useInfiniteQuery(linksOrganizationQueryOptions(listQuery))
	const rows = links.data?.links ?? EMPTY_LINKS
	const cursorExpired = getErrorCode(links.error) === 'cursor_invalid'
	const linkPolicy = useQuery(linkPolicyQueryOptions())

	async function loadNextPage() {
		const result = await infiniteQueryFetchNextPage(links)
		if (getErrorCode(result?.error) === 'cursor_invalid') {
			await queryClient.resetQueries({
				queryKey: makeQueryKeys.links.organization(listQuery)
			})
		}
	}

	return (
		<PageLayout
			actions={<RefreshButton query={links} />}
			bar={<AdminLinksFilters />}
			breadcrumb={<QueryBreadcrumbs />}
		>
			<div className='flex min-h-0 flex-1 flex-col overflow-hidden px-4 pb-6 md:px-6'>
				<AdminLinksTable
					data={rows}
					loadMore={{
						hasMore: links.hasNextPage,
						loading: links.isFetchingNextPage,
						onLoadMore: loadNextPage
					}}
					state={{
						empty: (
							<AdminLinksEmpty
								disabledByPolicy={
									linkPolicy.data?.allowed === false
								}
								filtered={
									params.kind !== 'all' ||
									params.creatorId !== undefined ||
									params.view !== 'live' ||
									params.expiresWithinDays !== undefined ||
									params.hasPassword !== undefined
								}
							/>
						),
						error: links.isError && !cursorExpired,
						loading: links.isPending || links.isPlaceholderData
					}}
				/>
			</div>
		</PageLayout>
	)
}

const EMPTY_LINKS: AdminLink[] = []
